import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useContract from "../hooks/useContract";

// --------------------
// BSC Mainnet
// --------------------
const BSC_CHAIN_ID = "0x38"; // 56

// --------------------
// BSC Testnet
// --------------------
const bscTestnet = {
  id: 97,
  name: "BSC Testnet",
  network: "bsc-testnet",
  rpcUrl: "https://data-seed-prebsc-1-s1.binance.org:8545/",
  nativeCurrency: {
    name: "Binance Coin",
    symbol: "BNB",
    decimals: 18,
  },
  blockExplorerUrl: "https://testnet.bscscan.com/",
};

const Navbar = () => {
  const location = useLocation();

  const { isAdmin } = useContract();

  const [openMenu, setOpenMenu] = useState(false);
  const [admin, setAdmin] = useState(false);

  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState(null);

  // --------------------------------------------------
  // GET WALLET PROVIDER
  // --------------------------------------------------
  const getProvider = () => {
    // Bitget Wallet
    if (window.bitkeep?.ethereum) {
      return window.bitkeep.ethereum;
    }

    // Other injected wallets
    if (window.ethereum) {
      return window.ethereum;
    }

    return null;
  };

  // --------------------------------------------------
  // CHECK WALLET CONNECTION
  // --------------------------------------------------
  const checkWalletConnection = async () => {
    try {
      const provider = getProvider();

      if (!provider) {
        setIsConnected(false);
        setAddress(null);
        return;
      }

      const accounts = await provider.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Failed to check wallet:", error);

      setAddress(null);
      setIsConnected(false);
    }
  };

  // --------------------------------------------------
  // CONNECT WALLET
  // --------------------------------------------------
  const connectWallet = async () => {
    try {
      const provider = getProvider();

      if (!provider) {
        alert(
          "No wallet detected. Please open this website inside Bitget Wallet or install a Web3 wallet."
        );
        return;
      }

      // Request accounts
      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });

      if (!accounts || accounts.length === 0) {
        return;
      }

      const walletAddress = accounts[0];

      setAddress(walletAddress);
      setIsConnected(true);

      // --------------------------------------------------
      // CHECK CURRENT NETWORK
      // --------------------------------------------------
      const chainId = await provider.request({
        method: "eth_chainId",
      });

      // --------------------------------------------------
      // SWITCH TO BSC MAINNET
      // --------------------------------------------------
      if (chainId.toLowerCase() !== BSC_CHAIN_ID) {
        try {
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [
              {
                chainId: BSC_CHAIN_ID,
              },
            ],
          });
        } catch (switchError) {
          console.error(
            "BSC network switch failed:",
            switchError
          );

          // --------------------------------------------------
          // BSC IS NOT ADDED TO WALLET
          // --------------------------------------------------
          if (switchError.code === 4902) {
            try {
              await provider.request({
                method: "wallet_addEthereumChain",
                params: [
                  {
                    chainId: BSC_CHAIN_ID,
                    chainName: "BNB Smart Chain",
                    nativeCurrency: {
                      name: "BNB",
                      symbol: "BNB",
                      decimals: 18,
                    },
                    rpcUrls: [
                      "https://bsc-dataseed.binance.org/",
                    ],
                    blockExplorerUrls: [
                      "https://bscscan.com/",
                    ],
                  },
                ],
              });
            } catch (addError) {
              console.error(
                "Failed to add BSC network:",
                addError
              );

              return;
            }
          } else {
            return;
          }
        }
      }

      // --------------------------------------------------
      // CHECK AGAIN AFTER NETWORK SWITCH
      // --------------------------------------------------
      const finalAccounts = await provider.request({
        method: "eth_accounts",
      });

      if (finalAccounts && finalAccounts.length > 0) {
        setAddress(finalAccounts[0]);
        setIsConnected(true);
      }
    } catch (error) {
      console.error(
        "Wallet connection failed:",
        error
      );

      // User rejected connection
      if (error?.code === 4001) {
        console.log(
          "User rejected the wallet connection."
        );
      }

      setIsConnected(false);
      setAddress(null);
    }
  };

  // --------------------------------------------------
  // DISCONNECT WALLET
  // --------------------------------------------------
  const disconnectWallet = () => {
    // Clear DApp connection state
    setAddress(null);
    setIsConnected(false);
    setAdmin(false);

    // Close mobile menu
    setOpenMenu(false);

    console.log("Wallet disconnected from the DApp");
  };

  // --------------------------------------------------
  // WALLET BUTTON
  // --------------------------------------------------
  const handleWalletClick = async () => {
    if (!isConnected) {
      await connectWallet();
      return;
    }

    // When already connected,
    // clicking the desktop button disconnects.
    disconnectWallet();
  };

  // --------------------------------------------------
  // INITIAL WALLET CHECK
  // --------------------------------------------------
  useEffect(() => {
    checkWalletConnection();
  }, []);

  // --------------------------------------------------
  // WALLET EVENTS
  // --------------------------------------------------
  useEffect(() => {
    const provider = getProvider();

    if (!provider) {
      return;
    }

    // Account changed
    const handleAccountsChanged = (accounts) => {
      console.log(
        "Wallet accounts changed:",
        accounts
      );

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress(null);
        setIsConnected(false);
        setAdmin(false);
      }
    };

    // Network changed
    const handleChainChanged = (chainId) => {
      console.log(
        "Network changed:",
        chainId
      );

      // Re-check account
      checkWalletConnection();
    };

    provider.on(
      "accountsChanged",
      handleAccountsChanged
    );

    provider.on(
      "chainChanged",
      handleChainChanged
    );

    return () => {
      if (provider.removeListener) {
        provider.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        provider.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, []);

  // --------------------------------------------------
  // ADMIN CHECK
  // --------------------------------------------------
  useEffect(() => {
    if (!address) {
      setAdmin(false);
      return;
    }

    const check = async () => {
      try {
        const res = await isAdmin();

        setAdmin(res);
      } catch (error) {
        console.error(
          "Admin check failed:",
          error
        );

        setAdmin(false);
      }
    };

    check();
  }, [address, isAdmin]);

  // --------------------------------------------------
  // MENU
  // --------------------------------------------------
  const menu = [
    {
      path: "/",
      label: "Home",
    },
    {
      path: "/cards",
      label: "Cards",
    },
    {
      path: "/dashboard",
      label: "Dashboard",
    },
    {
      path: "/earnings",
      label: "Earnings",
    },
    {
      path: "/withdraw",
      label: "Withdraw",
    },
    ...(admin
      ? [
          {
            path: "/admin",
            label: "Admin",
          },
        ]
      : []),
  ];

  // --------------------------------------------------
  // SHORT ADDRESS
  // --------------------------------------------------
  const shortAddress =
    address
      ? `${address.slice(0, 4)}...${address.slice(-4)}`
      : "Connect Wallet";

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div className="w-full border-b border-[#2A2A2A] bg-[#111111] text-white relative">

      {/* HEADER */}
      <div className="flex items-center justify-between px-5 md:px-8 py-4">

        {/* LOGO */}
        <Link
          to="/"
          className="flex items-center gap-2"
        >
          <img
            src="/kindlogo.png"
            className="h-10"
            alt="KindCat logo"
          />

          <span className="text-xl font-bold text-[#D3AF37]">
            KindCat
          </span>
        </Link>

        {/* DESKTOP MENU */}
        <div className="hidden md:flex gap-6">

          {menu.map((item) => {
            const active =
              location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-xl transition ${
                  active
                    ? "bg-[#D3AF37] text-white"
                    : "text-white hover:bg-[#1A1A1A] hover:text-[#D3AF37]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}

        </div>

        {/* RIGHT SIDE */}
        <div className="flex items-center gap-3">

          {/* DESKTOP WALLET BUTTON */}
          <button
            onClick={handleWalletClick}
            className="hidden md:block px-5 py-2.5 rounded-xl font-semibold bg-[#D3AF37] text-white hover:bg-[#B59410] transition-colors"
          >
            {isConnected && address
              ? shortAddress
              : "Connect Wallet"}
          </button>

          {/* MOBILE MENU BUTTON */}
          <button
            onClick={() => setOpenMenu(true)}
            className="md:hidden text-[#D3AF37] text-2xl"
          >
            ☰
          </button>

        </div>
      </div>

      {/* OVERLAY */}
      {openMenu && (
        <div
          onClick={() => setOpenMenu(false)}
          className="fixed inset-0 bg-black/50 z-40"
        />
      )}

      {/* MOBILE MENU */}
      <div
        className={`fixed top-0 right-0 h-[100dvh] w-[80%] max-w-sm bg-[#111111] z-50 transform transition-transform duration-300 flex flex-col border-l border-[#2A2A2A] ${
          openMenu
            ? "translate-x-0"
            : "translate-x-full"
        }`}
      >

        {/* MOBILE HEADER */}
        <div className="flex justify-between items-center p-5 border-b border-[#2A2A2A]">

          <h2 className="text-white text-lg">
            Menu
          </h2>

          <button
            onClick={() => setOpenMenu(false)}
            className="text-white text-2xl"
          >
            ✕
          </button>

        </div>

        {/* MOBILE LINKS */}
        <div className="flex flex-col gap-3 p-5 flex-1 overflow-y-auto">

          {menu.map((item) => {
            const active =
              location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpenMenu(false)}
                className={`px-4 py-3 rounded-xl transition ${
                  active
                    ? "bg-[#D3AF37] text-white"
                    : "text-white hover:bg-[#1A1A1A] hover:text-[#D3AF37]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}

        </div>

        {/* MOBILE WALLET */}
        <div className="p-5 border-t border-[#2A2A2A]">

          <button
            onClick={async () => {
              if (isConnected) {
                disconnectWallet();
              } else {
                await connectWallet();
                setOpenMenu(false);
              }
            }}
            className="w-full py-3 rounded-xl font-semibold bg-[#D3AF37] text-white hover:bg-[#B59410] transition-colors"
          >
            {isConnected && address
              ? `Disconnect ${shortAddress}`
              : "Connect Wallet"}
          </button>

        </div>
      </div>
    </div>
  );
};

export default Navbar;